import api from 'src/store/api'
import dates from 'src/utils/dates'
import http from 'src/store/http'

import { firecollection } from 'src/store/firebase'

import Router from 'src/router/index'

import actionNames from './actionNames'
import User from './utils/user'

const actionMap = {
  organization: {
    CREATE: 'createOrganization',
    READ: 'auth/getOrgData',
    UPDATE: 'updateOrganization',
    DELETE: 'deleteOrganization',
  },
  person: {
    CREATE: 'commitPersonCreate',
    READ: '',
    UPDATE: 'commitPersonUpdate',
    DELETE: 'commitPersonDelete',
  },
  personApi: {
    CREATE: 'createPerson',
    READ: '',
    UPDATE: 'updatePerson',
    DELETE: 'deletePerson',
  },
  project: {
    CREATE: 'createProject',
    READ: '',
    UPDATE: 'updateProject',
    DELETE: 'deleteProject',
  },
  time: {
    READ: '',
  },
}

const actions = {
  setup() {},

  async getData({ dispatch }, reportKey) {
    return dispatch('tools/setView', reportKey, { root: true })
  },

  mapProject(context, project) {
    const { projectCode, projectId, projectName } = project

    return {
      ...project,

      // Prepare database properties.
      project_code: projectCode,
      project_id: projectId,
      project_name: projectName,
    }
  },
  async onEditProject({ commit, dispatch }, row) {
    const project = await dispatch('mapProject', row)

    commit('selected', project)
    commit('entry', project)

    commit('action', 'update')

    commit('isProjectModalVisible', true)
  },
  async onDeleteProject({ commit, dispatch }, row) {
    const project = await dispatch('mapProject', row)

    commit('selected', project)
    commit('entry', project)

    commit('action', 'delete')

    commit('isProjectModalVisible', true)
  },

  async updateTimeEntries({ commit, dispatch, rootGetters, rootState, state }, { endpointName, moduleName }) {
    const { auth } = rootState
    const { user } = auth

    const reportKey = rootGetters['tools/reportKey']
    const body = {
      person_email: user.email,
      include_delegates: false,
    }

    const response = await api.getEntries(body)
    const { content, error } = response
    const root = { root: true }

    if (!error) {
      const { summaries } = state
      const items = await dispatch('tools/transformTimeItems', content, root)
      const itemsReport = { endpointName, items, moduleName, reportKey, summaries }

      commit('tools/items', itemsReport, root)
      dispatch('tools/summaries', itemsReport, root)

      dispatch('tools/setView', reportKey, root)
    } else {
      const message = error || 'View update erred. Reload to view the latest time entries.'

      commit('tools/snackbar', true, root)
      commit('tools/snack', { message, status: 'error' }, root)
    }

    return response
  },

  async getProject({ dispatch, rootGetters }, reportId) {
    const endpointName = 'project'
    const reportKey = rootGetters['tools/reportKey']
    const root = { root: true }
    const applyFilters = false
    const requestReport = { applyFilters, endpointName, reportKey }

    const handler = await dispatch('tools/requestHandler', requestReport, root)
    const { catcher, success } = handler

    const isReportIdBillingCode = rootGetters['tools/isReportIdBillingCode']
    const request = isReportIdBillingCode
      ? api.getProjectByCode(reportId)
      : api.getProjectById(reportId)

    const response = await request.catch(catcher)
    const { content: items, error } = response

    if (error) return catcher(response)

    const moduleName = 'project'
    const responseReport = { endpointName, items, moduleName, success }

    return dispatch('tools/requestSuccess', responseReport, root)
  },

  async openTimeEntryFormCreate({ commit, state, rootGetters, rootState }, weekdate) {
    const { defaultEntry } = state
    const persons = rootGetters['tools/persons']
    const { user } = rootState.auth

    const defaults = Object.create(defaultEntry)
    const thisPerson = persons.find((person) => person.personEmail === user.email)
    const time_entry_date = dates.getFormattedDate(weekdate, 'YYYY-MM-DD')

    const entry = {
      ...defaults,
      person_id: thisPerson.personId,
      time_entry_date,
      time_classification_id: 'NON-BILLABLE',
    }

    commit('action', 'create')
    commit('weekdate', weekdate)

    commit('selected', entry)
    commit('entry', entry)

    commit('isTimeModalVisible', true)
  },
  openTimeEntryFormUpdate({ commit }, entry) {
    commit('action', 'update')

    commit('selected', entry)
    commit('entry', entry)

    commit('isTimeModalVisible', true)
  },
  openTimeEntryFormDelete({ commit }, entry) {
    commit('action', 'delete')

    commit('selected', entry)
    commit('entry', entry)

    commit('isTimeModalVisible', true)
  },

  createTimeEntry({ commit }, entry) {
    entry.time_entry_date = dates.getFormattedDate(entry.time_entry_date, 'YYYY-MM-DD')

    return api.createTimeEntry(entry).catch((error) => {
      commit(actionNames.ADD_TIME_FAILED, error)
    })
  },
  updateTimeEntry({ commit, rootState }, entry) {
    const { auth } = rootState
    const { user } = auth
    const { email: person_email } = user

    const data = {
      client_id: entry.client_id,
      description: entry.description,
      duration: entry.duration,
      person_email,
      project_id: entry.project_id,
      service_id: entry.service_id,
      time_classification_id: entry.time_classification_id,
      time_entry_date: dates.getFormattedDate(entry.time_entry_date, 'MM-DD-YYYY'),
      time_entry_id: entry.timeEntryId,
    }

    return api.updateTimeEntry(data).catch((error) => {
      commit(actionNames.EDIT_TIME_FAILED, error)
    })
  },
  deleteTimeEntry({ commit, rootState }, entry) {
    const { auth } = rootState
    const { user } = auth
    const { email: person_email } = user
    const { timeEntryId: time_entry_id } = entry

    const data = {
      person_email,
      time_entry_id,
    }

    return api.deleteTimeEntry(data).catch((error) => {
      commit(actionNames.DELETE_TIME_FAILED, error)
    })
  },
  async submitTimeEntry({ dispatch, rootGetters, state }, entry) {
    const reportKey = rootGetters['tools/reportKey']
    const { action } = state
    let items

    if (action === 'create') {
      items = await dispatch('createTimeEntry', entry)
    }
    if (action === 'update') {
      items = await dispatch('updateTimeEntry', entry)
    }
    if (action === 'delete') {
      items = await dispatch('deleteTimeEntry', entry)
    }

    dispatch('updateTimeEntries', { endpointName: 'times', moduleName: reportKey })

    return items
  },

  createItem({ dispatch, rootState }, weekdate) {
    const { tools } = rootState
    const { reportName } = tools

    if (reportName === 'organization') {
      return dispatch('createOrganizationForm')
    }

    if (reportName === 'person') {
      return dispatch('createPersonForm')
    }

    if (reportName === 'project') {
      return dispatch('createProjectForm')
    }

    return dispatch('openTimeEntryFormCreate', weekdate)
  },

  showFormOrganization({ commit, dispatch }, { action = 'create', formData }) {
    dispatch('setFormData', { action, formData })
    commit('isOrganizationModalVisible', true)
  },
  showFormOrganizationUpdate({ dispatch }, formData) {
    dispatch('showFormOrganization', { action: 'update', formData })
  },
  showFormOrganizationDelete({ dispatch }, formData) {
    dispatch('showFormOrganization', { action: 'delete', formData })
  },
  goToOrganization(context, organization) {
    const { billingCode } = organization
    Router.push(`/internal/organization/${billingCode}`)
  },

  mapOrgToClient(context, organization) {
    const {
      action_type,
      billingCode,
      clientId,
      clientName,
      publisherName,
      isAdministrative,
    } = organization

    return {
      action_type,
      billing_code: billingCode,
      client_id: clientId,
      client_name: clientName || publisherName,
      is_administrative: isAdministrative,
    }
  },
  mapEntryToClient(context, organization) {
    const {
      actionType,
      billingCode,
      clientId,
      clientName,
      isAdministrative,
    } = organization

    return {
      action_type: actionType,
      billing_code: billingCode,
      client_id: clientId,
      client_name: clientName,
      is_administrative: isAdministrative,
    }
  },
  async createOrganizationForm({ dispatch, state }) {
    const { defaultOrg } = state
    const formData = await dispatch('mapOrgToClient', defaultOrg)

    dispatch('showFormOrganization', { action: 'create', formData })
  },
  async modifyOrganization({ commit, dispatch, getters, rootState }, { events, request }) {
    const { reportKey } = getters
    const { succeeded } = events
    const root = { root: true }

    const catcher = (err) => {
      const error = http.catch(err)
      dispatch('messages/showErrorMessage', { error, reportKey }, root)
      return error
    }
    const response = await api.modifyOrganization(request).catch(catcher)
    const { content, error } = response

    if (error) {
      catcher(error)
    } else {
      const { user } = rootState.auth
      const moduleName = 'organization'

      dispatch('auth/getOrganizations', { moduleName, user }, { root: true })

      commit(succeeded, content)
    }

    return response
  },
  async createOrganization({ dispatch, getters }, organization) {
    const { reportKey } = getters
    const root = { root: true }
    dispatch('messages/showLoadingMessage', { reportKey, text: 'Requesting data ...' }, root)

    const request = await dispatch('mapEntryToClient', organization)

    const failed = actionNames.ADD_ORGANIZATION_FAILED
    const succeeded = actionNames.ADD_ORGANIZATION_SUCCEEDED

    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyOrganization', modify)
  },
  async updateOrganization({ commit, dispatch }, organization) {
    commit(actionNames.EDIT_ORGANIZATION_REQUESTED)

    const request = await dispatch('mapEntryToClient', organization)

    const failed = actionNames.EDIT_ORGANIZATION_FAILED
    const succeeded = actionNames.EDIT_ORGANIZATION_SUCCEEDED

    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyOrganization', modify)
  },
  async deleteOrganization({ commit, dispatch }, organization) {
    commit(actionNames.DELETE_ORGANIZATION_REQUESTED)

    const { actionType, clientId } = organization
    const request = { action_type: actionType, client_id: clientId }

    const failed = actionNames.DELETE_ORGANIZATION_FAILED
    const succeeded = actionNames.DELETE_ORGANIZATION_SUCCEEDED

    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyOrganization', modify)
  },

  setFormData({ commit }, { action, formData }) {
    // The `selected` represents the copy on which we'll act.
    // The `entry` represents the original object, which we can use to restore the form on click of Cancel.
    commit('selected', { ...formData })
    commit('entry', formData)

    commit('action', action)
  },

  showFormPerson({ commit, dispatch }, { action = 'create', formData }) {
    dispatch('setFormData', { action, formData })
    commit('isPersonModalVisible', true)
  },
  async showFormPersonCopy({ dispatch, rootState, state }, copyData) {
    const { defaultPerson, defaultUser } = state
    const { searchData } = rootState.tools
    const defaultPersonUser = { ...defaultPerson, ...defaultUser }
    const organizationsResponse = await searchData.organizations
    const { content: organizations = [] } = organizationsResponse || {}

    const personUser = {
      ...copyData,
      docId: defaultPersonUser.docId,
      email: defaultPersonUser.email,
      personId: defaultPersonUser.personId,
      personNameFirst: defaultPersonUser.personNameFirst,
      personNameLast: defaultPersonUser.personNameLast,
      personEmail: defaultPersonUser.personEmail,
    }

    const formData = await User.mapPersonAndUser(personUser, organizations)

    dispatch('showFormPerson', { action: 'create', formData })
  },
  async showFormPersonCreate({ dispatch, state }) {
    const { defaultPerson, defaultUser } = state
    const personUser = { ...defaultPerson, ...defaultUser }
    const organizations = []
    const formData = await User.mapPersonAndUser(personUser, organizations)

    return dispatch('showFormPerson', { action: 'create', formData })
  },
  showFormPersonUpdate({ dispatch }, formData) {
    dispatch('showFormPerson', { action: 'update', formData })
  },
  showFormPersonDelete({ dispatch }, formData) {
    dispatch('showFormPerson', { action: 'delete', formData })
  },
  goToPerson(context, person) {
    const { docId } = person
    Router.push(`/internal/person/${docId}`)
  },

  mapPerson(context, personUser) {
    const {
      action_type,
      access = {},
      managerPersonId,
      email = '',
      personId: person_id,
      personNameFirst = '',
      personNameLast = '',
    } = personUser
    const { active = false, level, role } = access
    const is_admin = level === 'admin'
    const is_internal = role === 'internal'
    const manager_person_id = (is_internal && managerPersonId) || undefined
    const apiActionTypes = {
      CREATE: 'ADD',
      UPDATE: 'UPDATE',
      DELETE: 'DELETE',
    }

    const email_person = (email || '').toLowerCase().trim()
    const person_name_first = (personNameFirst || '').trim()
    const person_name_last = (personNameLast || '').trim()

    const user = {
      action_type: apiActionTypes[action_type],
      email_person,
      is_active: active,
      is_admin,
      is_internal,
      manager_person_id,
      person_name_first,
      person_name_last,
    }

    if (person_id !== email_person) {
      user.person_id = person_id
    }

    return user
  },

  async createPersonForm({ dispatch }) {
    return dispatch('showFormPersonCreate')
  },
  async upsertPersonAndUser({ dispatch, rootGetters }, data) {
    const { action, catcher, person } = data
    const hasRightsToUpdate = rootGetters['auth/isInternalWriter']

    if (!hasRightsToUpdate) {
      const metadata = { email: person.email }
      const error = { code: '403', layer: 'app', message: 'Forbidden', metadata }
      return catcher({ error })
    }

    const setUserForFirebase = dispatch('auth/upsertUser', person, { root: true })

    const updateApiPerson = () => {
      const missingRequirements = !person.person_id && action === 'update'
      const safeAction = missingRequirements ? 'create' : action
      const actionRequest = { action: safeAction, catcher, person }
      const crudAction = action.toUpperCase()
      const actionName = actionMap.personApi[crudAction]

      return dispatch(actionName, actionRequest)
    }
    const setPersonForApi = updateApiPerson()

    const [apiRes] = await Promise.all([setPersonForApi, setUserForFirebase])
    const { content, error } = apiRes || {}

    if (error) catcher(apiRes)

    const [apiPerson] = Array.isArray(content) ? content : [content]
    const user = await firecollection.get('person', person.email)

    return { error, person: apiPerson, user }
  },

  async createPerson({ dispatch }, { catcher, person }) {
    const request = await dispatch('mapPerson', person)

    return api.modifyPerson(request).catch(catcher)
  },
  async updatePerson({ dispatch }, { catcher, person }) {
    const request = await dispatch('mapPerson', person)

    return api.modifyPerson(request).catch(catcher)
  },
  async deletePerson(context, { catcher, person }) {
    const { action_type, personId: person_id } = person

    const request = {
      action_type,
      person_id,
    }

    return api.modifyPerson(request).catch(catcher)
  },

  async commitPersonCreate({ commit, dispatch }, person) {
    commit(actionNames.ADD_PERSON_REQUESTED)

    const catcher = (caughtError) => {
      const error = http.catch(caughtError)
      commit(actionNames.ADD_PERSON_FAILED, error)
      return error
    }
    const action = 'CREATE'
    const request = { action, catcher, person }

    const personUser = await dispatch('upsertPersonAndUser', request)
    const { error: personError, person: apiPerson, user } = personUser

    if (personError) return catcher(personUser)

    const content = apiPerson ? { ...apiPerson, ...user } : user

    commit(actionNames.ADD_PERSON_SUCCEEDED, content)

    const reportKey = 'person'

    dispatch('tools/setView', reportKey, { root: true })

    return content
  },
  async commitPersonUpdate({ commit, dispatch }, person) {
    commit(actionNames.EDIT_PERSON_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.EDIT_PERSON_FAILED, error)
      return error
    }
    const action = 'UPDATE'
    const request = { action, catcher, person }

    const personUser = await dispatch('upsertPersonAndUser', request)
    const { error, person: apiPerson, user } = personUser

    if (error) return catcher(personUser)

    const content = apiPerson ? { ...apiPerson, ...user } : user
    const reportKey = 'person'

    dispatch('tools/setView', reportKey, { root: true })

    commit(actionNames.EDIT_PERSON_SUCCEEDED, content)

    return content
  },
  async commitPersonDelete({ commit, dispatch }, person) {
    commit(actionNames.DELETE_PERSON_REQUESTED)

    const catcher = (err) => {
      const error = http.catch(err)
      commit(actionNames.DELETE_PERSON_FAILED, error)
      return error
    }
    const action = 'DELETE'
    const request = { action, catcher, person }

    const personUser = await dispatch('upsertPersonAndUser', request)
    const { error, person: apiPerson, user } = personUser

    if (error) return catcher(personUser)

    const content = { ...apiPerson, ...user }
    const reportKey = 'person'

    dispatch('tools/setView', reportKey, { root: true })

    commit(actionNames.DELETE_PERSON_SUCCEEDED, content)

    return content
  },

  async updateUser({ dispatch, rootGetters }, data) {
    const { catcher, person } = data
    const hasRightsToUpdate = rootGetters['auth/isUserInternalAdmin']

    if (!hasRightsToUpdate) {
      const metadata = { email: person.email }
      const error = { code: '403', layer: 'app', message: 'Forbidden', metadata }
      return catcher({ error })
    }

    await dispatch('auth/upsertUser', person, { root: true })

    return firecollection.get('person', person.email)
  },

  showFormProject({ commit, dispatch }, { action = 'create', formData }) {
    dispatch('setFormData', { action, formData })
    commit('isProjectModalVisible', true)

    return true
  },
  showFormProjectUpdate({ dispatch }, formData) {
    return dispatch('showFormProject', { action: 'update', formData })
  },
  showFormProjectDelete({ dispatch }, formData) {
    return dispatch('showFormProject', { action: 'delete', formData })
  },
  goToProject(context, project) {
    const { projectCode } = project
    Router.push(`/internal/project/${projectCode}`)
  },

  async createProjectForm({ dispatch, state }) {
    const { defaultProject } = state
    const formData = await dispatch('mapProject', defaultProject)

    return dispatch('showFormProject', { action: 'create', formData })
  },
  async modifyProject({ commit, dispatch, rootGetters }, { events, request }) {
    const { failed, succeeded } = events

    const catcher = (err) => {
      const error = http.catch(err)
      commit(failed, error)
      return error
    }
    const response = await api.modifyProject(request).catch(catcher)
    const { content, error } = response

    if (error) {
      catcher(error)
    } else {
      const reportKey = rootGetters['tools/reportKey']
      const columnsByKey = rootGetters['tools/columnsByKey']

      dispatch('setProjectView', { columnsByKey, params: { reportKey } })

      commit(succeeded, content)
    }

    return response
  },
  async createProject({ commit, dispatch }, project) {
    commit(actionNames.ADD_PROJECT_REQUESTED)

    const { action_type, project_code, project_name } = project
    const request = { action_type, project_code, project_name }
    const failed = actionNames.ADD_PROJECT_FAILED
    const succeeded = actionNames.ADD_PROJECT_SUCCEEDED
    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyProject', modify)
  },
  async updateProject({ commit, dispatch }, project) {
    commit(actionNames.EDIT_PROJECT_REQUESTED)

    const { action_type, project_id, project_code, project_name } = project
    const request = { action_type, project_id, project_code, project_name }
    const failed = actionNames.EDIT_PROJECT_FAILED
    const succeeded = actionNames.EDIT_PROJECT_SUCCEEDED
    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyProject', modify)
  },
  async deleteProject({ commit, dispatch }, project) {
    commit(actionNames.DELETE_PROJECT_REQUESTED)

    const { action_type, project_id } = project
    const request = { action_type, project_id }
    const failed = actionNames.DELETE_PROJECT_FAILED
    const succeeded = actionNames.DELETE_PROJECT_SUCCEEDED
    const events = { failed, succeeded }
    const modify = { events, request }

    return dispatch('modifyProject', modify)
  },

  async submitAction({ commit, dispatch }, actionRequest) {
    const { action, endpointName, value } = actionRequest
    const currentAction = actionMap[endpointName][action.toUpperCase()]

    if (!currentAction) {
      commit(actionNames.UNKNOWN_ERROR, 'action')

      return Promise.resolve(actionNames.UNKNOWN_ERROR)
    }

    return dispatch(currentAction, value)
  },
  submitOrgAction({ dispatch, state }, organization) {
    const { action } = state
    const endpointName = 'organization'
    const actionRequest = { action, endpointName, value: organization }

    return dispatch('submitAction', actionRequest)
  },
  async submitPersonAction({ dispatch, state }, value) {
    const { action } = state
    const endpointName = 'person'
    const actionRequest = { action, endpointName, value }

    return dispatch('submitAction', actionRequest)
  },
  submitProjectAction({ dispatch, state }, project) {
    const { action } = state
    const endpointName = 'project'
    const actionRequest = { action, endpointName, value: project }

    return dispatch('submitAction', actionRequest)
  },

  // Upsert a project document to the Firestore 'project' collection.
  upsertProjectToFirestore({ dispatch }, project) {
    const collection = 'project'
    const doc = { ...project }
    const docId = doc.projectCode || `${doc.billingCode}-P001`

    if (!doc.projectCode) {
      doc.projectCode = doc.docId
    }

    return dispatch('auth/upsertFirestore', { collection, docId, doc })
  },
}

export default actions
